import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { getBundle, bundles } from "@/lib/signupI18n";
import { getAuthMessages, rememberLocale } from "@/lib/authMessages";
import { getLaunchLocaleFromCookie } from "@/lib/launchLocale";
import SignUpPanel from "@/components/SignUpPanel";

const TAB_KEYS = ["signin", "signup", "forgot"];
const TAB_LABELS = {
  signin: "tabSignIn",
  signup: "tabSignUp",
  forgot: "tabForgot",
};

export const PRODUCT_TITLES = {
  en: "English Job Interview Simulator",
  "pt-BR": "Simulador de Entrevistas de Emprego em Inglês",
  "es-419": "Simulador de Entrevistas de Trabajo en Inglés",
  "fr-CA": "Simulateur d’entrevue d’emploi en anglais",
};

function goToGoogleSignIn() {
  const url = window.location.origin + "/api/auth/google/start";
  const embedded = window.self !== window.top;
  if (embedded) {
    const popup = window.open(url, "_blank");
    if (popup) {
      popup.opener = null;
      return;
    }
  }
  window.location.href = url;
}

export default function Login() {
  const [tab, setTab] = useState("signin");
  const [locale, setLocale] = useState(() => getLaunchLocaleFromCookie());
  const b = getBundle(locale);
  const authT = getAuthMessages(locale);
  const productTitle = PRODUCT_TITLES[locale] || PRODUCT_TITLES.en;
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [resetInfo, setResetInfo] = useState(null);
  const requestedReturnTo = location.state?.from?.pathname;
  const returnTo = requestedReturnTo || "/";

  useEffect(() => {
    rememberLocale(locale);
  }, [locale]);

  useEffect(() => {
    const code = new URLSearchParams(location.search).get("google_error");
    if (!code) return;
    if (code === "active_session_exists") setErr(authT.activeLoginExists);
    else if (code !== "cancelled") setErr(b.googleError || "");
    window.history.replaceState(null, "", window.location.pathname);
  }, [location.search, authT.activeLoginExists, b.googleError]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const authedUser = await login(email.trim(), password);
      const toastLocale = (authedUser && authedUser.preferred_language) || locale;
      const welcome = (bundles[toastLocale] || bundles.en).welcomeToast || bundles.en.welcomeToast;
      toast.success(welcome);
      const destination = !requestedReturnTo && authedUser?.is_admin ? "/admin" : returnTo;
      navigate(destination, { replace: true });
    } catch (ex) {
      setErr(formatApiErrorDetail(ex, locale));
    } finally {
      setBusy(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setErr("");
    setResetInfo(null);
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setResetInfo(data);
    } catch (ex) {
      setErr(formatApiErrorDetail(ex, locale));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-[color:var(--bg-base)]">
      <div className="w-full max-w-lg rounded-3xl bg-white shadow-xl border border-[color:var(--border-stone)] p-6 sm:p-8">
        <div className="text-xs uppercase tracking-[0.20em] text-[color:var(--brand)] mb-2 font-medium">
          {productTitle}
        </div>

        <div className="grid grid-cols-3 rounded-xl bg-[color:var(--bg-surface-hover)] p-1 text-xs" role="tablist" data-testid="login-tabs" data-locale={locale}>
          {TAB_KEYS.map((k) => (
            <button
              key={k}
              onClick={() => {
                setTab(k);
                setErr("");
                setResetInfo(null);
              }}
              className={`rounded-lg px-2 py-2 font-medium transition-colors ${tab === k ? "bg-white text-[color:var(--text-primary)] shadow-sm" : "text-[color:var(--text-secondary)] hover:text-[color:var(--brand)]"}`}
              data-testid={`login-tab-${k}`}
              role="tab"
              aria-selected={tab === k}
            >
              {b[TAB_LABELS[k]]}
            </button>
          ))}
        </div>

        {tab === "signin" && (
          <form className="mt-5 space-y-3" onSubmit={handleSignIn}>
            <LocalizedField label={b.emailLabel} type="email" value={email} onChange={setEmail} placeholder={b.emailPlaceholder} testId="email" autoComplete="email" />
            <PasswordField label={b.passwordLabel} value={password} onChange={setPassword} placeholder={b.signInPasswordPlaceholder} testId="password" autoComplete="current-password" showLabel={authT.showPassword} hideLabel={authT.hidePassword} />
            {err && <InlineError text={err} testId="login-error" />}
            <BasicSubmit busy={busy} busyLabel={authT.processing} label={b.signInSubmit} testId="signin-submit" />
          </form>
        )}

        {tab === "signup" && <SignUpPanel locale={locale} setLocale={setLocale} />}

        {tab === "forgot" && (
          <form className="mt-5 space-y-3" onSubmit={handleForgot}>
            <LocalizedField label={b.emailLabel} type="email" value={email} onChange={setEmail} placeholder={b.emailPlaceholder} testId="email" autoComplete="email" />
            <BasicSubmit busy={busy} busyLabel={authT.sending} label={b.forgotSubmit} testId="forgot-submit" />
            {resetInfo && (
              <div className="mt-2 rounded-xl bg-[color:var(--brand-accent)]/10 border border-[color:var(--brand-accent)]/40 p-3 text-xs text-[color:var(--text-primary)]" data-testid="forgot-result">
                <div className="font-medium">{authT.forgotConfirmation}</div>
              </div>
            )}
            {err && <InlineError text={err} testId="login-error" />}
          </form>
        )}

        <div className="my-5 flex items-center gap-3 text-xs text-[color:var(--text-secondary)]">
          <span className="h-px flex-1 bg-[color:var(--border-stone)]" />
          {b.orDivider}
          <span className="h-px flex-1 bg-[color:var(--border-stone)]" />
        </div>

        <button type="button" onClick={goToGoogleSignIn} className="w-full rounded-full border border-[color:var(--border-stone)] px-4 py-2.5 text-sm font-medium text-[color:var(--text-primary)] hover:bg-[color:var(--bg-surface-hover)] hover:border-[color:var(--brand-accent)]" data-testid="google-signin">
          {b.googleCta}
        </button>
        <p className="mt-2 text-[11px] text-[color:var(--text-secondary)] text-center leading-snug" data-testid="google-signin-hint">{b.googleHint}</p>

        <p className="mt-4 text-[11px] text-[color:var(--text-secondary)] text-center">
          <Link to="/reset-password" className="underline hover:text-[color:var(--brand)]" data-testid="switch-to-reset">{b.resetTokenLink}</Link>
        </p>

        <p className="mt-6 text-[10px] sm:text-[11px] text-[color:var(--text-secondary)] text-left">
          ©2026 Language Masters Software
        </p>
      </div>
    </div>
  );
}

function BasicSubmit({ busy, busyLabel, label, testId }) {
  return (
    <button type="submit" disabled={busy} className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-5 py-2.5 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-accent)] focus:ring-offset-2" data-testid={testId}>
      {busy ? busyLabel : label}
    </button>
  );
}

function LocalizedField({ label, type, value, onChange, placeholder, testId, autoComplete }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[color:var(--text-secondary)] mb-1">{label}</span>
      <input type={type} required value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full rounded-lg border border-[color:var(--border-stone)] bg-[color:var(--bg-surface-hover)] px-3 py-2 text-sm text-[color:var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-accent)] focus:bg-white" autoComplete={autoComplete} data-testid={testId} />
    </label>
  );
}

function PasswordField({ label, value, onChange, placeholder, testId, autoComplete, showLabel, hideLabel }) {
  const [visible, setVisible] = useState(false);
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[color:var(--text-secondary)] mb-1">{label}</span>
      <div className="relative">
        <input type={visible ? "text" : "password"} required value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full rounded-lg border border-[color:var(--border-stone)] bg-[color:var(--bg-surface-hover)] px-3 py-2 pr-20 text-sm text-[color:var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-accent)] focus:bg-white" autoComplete={autoComplete} data-testid={testId} />
        <button type="button" onClick={() => setVisible((v) => !v)} className="absolute inset-y-0 right-2 text-[11px] font-medium text-[color:var(--brand)] hover:text-[color:var(--brand-hover)]" aria-label={visible ? hideLabel : showLabel} data-testid={`${testId}-visibility`}>
          {visible ? hideLabel : showLabel}
        </button>
      </div>
    </label>
  );
}

function InlineError({ text, testId }) {
  return (
    <div className="rounded-lg bg-[color:var(--score-bad)]/10 border border-[color:var(--score-bad)]/30 p-2 text-xs text-[color:var(--score-bad)]" data-testid={testId}>
      {text}
    </div>
  );
}
